/**
 * Labstep
 *
 * @module components/File/Snapgene
 * @desc Component to show snapgene PNG/SVG
 */

import cx from 'classnames';
import { FileIFrameContainer as FileIFrame } from 'labstep-web/components/File/IFrame';
import { ThumbnailContainer } from 'labstep-web/containers/File/Thumbnail';
import { ToggleContainer } from 'labstep-web/containers/Toggle';
import { ModalDefaultContainer as Modal } from 'labstep-web/core/Modal/Default';
import type { File as FileModel } from 'labstep-web/models/file.model';
import type React from 'react';
import Lightbox from 'react-image-lightbox';
import { Image } from 'semantic-ui-react';
import styles from './styles.module.scss';
import type {
  FileFileSnapgenePreviewProps,
  FileSnapgenePreviewMessageProps,
  FileSnapgeneProps,
  FileSnapgeneToggleContainerProps,
} from './types';

export const Message: React.FC<FileSnapgenePreviewMessageProps> = ({
  message,
}) => <div className={styles.container}>{message}</div>;

export const canRenderSnapgene = (file: FileModel) =>
  Boolean(file.snapgene_output_png);

export const FileSnapgenePreview: React.FC<
  FileFileSnapgenePreviewProps
> = ({ file, toggle }) => {
  if (!canRenderSnapgene(file)) {
    return null;
  }
  return (
    <>
      {!file.snapgene_output_svg ? (
        <ThumbnailContainer file={file.snapgene_output_png}>
          {({ src }) => (
            <Lightbox mainSrc={src} onCloseRequest={toggle} />
          )}
        </ThumbnailContainer>
      ) : (
        <Modal
          viewComponent={null}
          size="fullscreen"
          header="Snapgene"
          initialModalState
          content={() => (
            <FileIFrame file={file.snapgene_output_svg} />
          )}
        />
      )}
    </>
  );
};

export const FileSnapgene: React.FC<FileSnapgeneProps> = ({
  file,
  toggle,
  toggled,
  expandable,
  ...rest
}) => {
  if (file.is_snapgene_processing) {
    return (
      <Message
        message={
          <p>
            Processing {file.name}.<br />
            Please wait...
            <br />
            It should take about 2 minutes.
          </p>
        }
      />
    );
  }

  if (!file.snapgene_output_png) {
    return (
      <Message
        message={
          <p>
            SnapGene server could not output any PNG or SVG from this
            data.
          </p>
        }
      />
    );
  }

  return (
    <>
      <ThumbnailContainer file={file.snapgene_output_png}>
        {({ src }) => (
          <Image
            fluid
            src={src}
            {...rest}
            className={cx(styles.image)}
            onClick={expandable ? toggle : undefined}
          />
        )}
      </ThumbnailContainer>
      {toggled && <FileSnapgenePreview file={file} toggle={toggle} />}
    </>
  );
};

FileSnapgene.defaultProps = {
  expandable: true,
};

export const FileSnapgeneToggleContainer: React.FC<
  FileSnapgeneToggleContainerProps
> = (props) => (
  <ToggleContainer>
    {(containerProps) => (
      <FileSnapgene {...props} {...containerProps} />
    )}
  </ToggleContainer>
);
